import { createFileRoute, Link } from "@tanstack/react-router";
import { Suspense, lazy } from "react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Clock, Globe, RotateCcw, ShieldCheck, Sparkles, Truck } from "lucide-react";
import { Layout } from "@/components/Layout";
import { ProductCard } from "@/components/ProductCard";
import { StickyMobileCta } from "@/components/StickyMobileCta";
import { AgeCards, CategoryCards } from "@/components/home/Discovery";
import { trackHeroCta, trackRailInteraction } from "@/lib/gtag";
import { ComparisonSkeleton } from "@/components/ProductComparison";
import { TrendingSkeleton } from "@/components/home/TrendingNow";
import {
  PRODUCTS_QUERY,
  storefrontApiRequest,
  type ShopifyProduct,
} from "@/lib/shopify";
import { POSTS } from "@/lib/montessori-koulu-posts";
import { CATEGORY_HERO_CARD } from "@/lib/article-heroes-card";
// @ts-expect-error vite-imagetools transforms query at build time
import newArticleWebp from "@/assets/hub/montessori-suomessa-2026.jpg?format=webp&w=900&quality=78";
// @ts-expect-error vite-imagetools transforms query at build time
import newArticleAvif from "@/assets/hub/montessori-suomessa-2026.jpg?format=avif&w=900&quality=62";

// Below-the-fold comparison tool: its own chunk so the homepage's critical
// JS stays small on mobile.
const ProductComparison = lazy(() => import("@/components/ProductComparison"));

// FAQ sits at the very bottom of the page — keep it out of the initial bundle.
const HomeFaq = lazy(() =>
  import("@/components/HomeFaq").then((m) => ({ default: m.HomeFaq })),
);

// @ts-expect-error vite-imagetools transforms query at build time
import heroWideAvif from "@/assets/hero-sunlit.jpg?format=avif&w=1920&quality=82";
// @ts-expect-error vite-imagetools transforms query at build time
import heroWideAvifMd from "@/assets/hero-sunlit.jpg?format=avif&w=1200&quality=80";
// @ts-expect-error vite-imagetools transforms query at build time
import heroWideAvifXl from "@/assets/hero-sunlit.jpg?format=avif&w=1440&quality=80";
// @ts-expect-error vite-imagetools transforms query at build time
import heroWideAvifSm from "@/assets/hero-sunlit.jpg?format=avif&w=768&quality=78";
// @ts-expect-error vite-imagetools transforms query at build time
import heroWideWebp from "@/assets/hero-sunlit.jpg?format=webp&w=1920&quality=86";
// @ts-expect-error vite-imagetools transforms query at build time
import heroWideWebpMd from "@/assets/hero-sunlit.jpg?format=webp&w=1200&quality=85";
// @ts-expect-error vite-imagetools transforms query at build time
import heroWideWebpXl from "@/assets/hero-sunlit.jpg?format=webp&w=1440&quality=85";
// @ts-expect-error vite-imagetools transforms query at build time
import heroWideWebpSm from "@/assets/hero-sunlit.jpg?format=webp&w=768&quality=84";
// @ts-expect-error vite-imagetools transforms query at build time
import lifestyleAvif from "@/assets/lifestyle-pikler.jpg?format=avif&w=1200&quality=78";
// @ts-expect-error vite-imagetools transforms query at build time
import lifestyleWebp from "@/assets/lifestyle-pikler.jpg?format=webp&w=1200&quality=82";

export const Route = createFileRoute("/")({
  loader: async ({ context }) => {
    // Fire the product fetches without awaiting: the hero (LCP element) is
    // static markup and must flush immediately. The grids hydrate after.
    void context.queryClient.prefetchQuery(bestsellerQueryOptions);
    void context.queryClient.prefetchQuery(piklerProductsQueryOptions);
  },
  head: () => ({
    meta: [
      { title: "Montessori-lelut, Pikler-kolmiot ja lasten kalusteet | Montessori24" },
      { name: "description", content: "Huolella valittuja Montessori-kalusteita, Pikler-kiipeilytelineitä ja kehittäviä leikkivälineitä 0–6-vuotiaille. Suomalainen verkkokauppa, toimitus 3–7 päivässä." },
      { property: "og:title", content: "Montessori-lelut, Pikler-kolmiot ja lasten kalusteet | Montessori24" },
      { property: "og:description", content: "Pikler-kiipeilytelineitä, oppimistorneja ja Montessori-leluja 0–6-vuotiaille. Suomalainen verkkokauppa, ilmainen toimitus Suomeen." },
      { property: "og:url", content: "https://montessori24.fi/" },
      { property: "og:image", content: "https://montessori24.fi/og-image.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "Montessori24 — Pikler-kiipeilytelineet ja Montessori-huonekalut" },
      { name: "twitter:image", content: "https://montessori24.fi/og-image.jpg" },
    ],
    links: [
      { rel: "canonical", href: "https://montessori24.fi/" },
      { rel: "alternate", hrefLang: "fi", href: "https://montessori24.fi/" },
      { rel: "alternate", hrefLang: "en-US", href: "https://montessori24.fi/us" },
      { rel: "alternate", hrefLang: "x-default", href: "https://montessori24.fi/" },
      {
        rel: "preload",
        as: "image",
        href: heroWideAvifMd,
        imageSrcSet: `${heroWideAvifSm} 768w, ${heroWideAvifMd} 1200w, ${heroWideAvifXl} 1440w, ${heroWideAvif} 1920w`,
        imageSizes: "100vw",
        type: "image/avif",
        fetchPriority: "high",
      },
    ],
    scripts: [],
  }),
  component: Index,
});

// CTA hierarchy: primary = filled, secondary = outline, tertiary = text link.
const ctaPrimary =
  "inline-flex min-h-[48px] items-center justify-center gap-2 rounded-[6px] bg-primary px-8 py-3.5 text-[15px] font-medium text-primary-foreground transition-all duration-300 hover:brightness-125 active:scale-[0.99]";
const ctaSecondary =
  "inline-flex min-h-[48px] items-center justify-center gap-2 rounded-[6px] border border-foreground/25 px-7 py-3.5 text-[15px] font-medium text-foreground transition-colors duration-300 hover:border-foreground/60 hover:bg-foreground/5";
const ctaText =
  "inline-flex min-h-[44px] items-center gap-2 text-[15px] font-medium text-foreground underline-offset-[6px] transition-colors hover:underline";

const bestsellerQueryOptions = {
  queryKey: ["products", "home-bestsellers"] as const,
  queryFn: async () => {
    try {
      const res = await storefrontApiRequest(PRODUCTS_QUERY, {
        first: 20,
        query:
          "tag:suosittu OR tag:bestseller OR title:oppimistorni OR title:kiipeilykolmio OR title:pikler",
      });
      return (res?.data?.products?.edges ?? []) as ShopifyProduct[];
    } catch (err) {
      console.error("[home] bestseller fetch failed:", err);
      return [] as ShopifyProduct[];
    }
  },
};

const piklerProductsQueryOptions = {
  queryKey: ["products", "popular-pikler"] as const,
  queryFn: async () => {
    try {
      const res = await storefrontApiRequest(PRODUCTS_QUERY, {
        first: 20,
        query: "pikler OR kiipeily OR kolmio",
      });
      return (res?.data?.products?.edges ?? []) as ShopifyProduct[];
    } catch (err) {
      console.error("[home] pikler fetch failed:", err);
      return [] as ShopifyProduct[];
    }
  },
};

const hasGoodImage = (p: ShopifyProduct) => !!p.node.images.edges[0]?.node.url;

/**
 * Short, non-promissory benefit line per product family. These describe what
 * the product type is for — no claims, ratings or invented specifications.
 */
function benefitFor(p: ShopifyProduct): string | undefined {
  const s = `${p.node.handle ?? ""} ${p.node.title}`.toLowerCase();
  if (/pikler|kiipeily|climb|triangle|kolmio/.test(s))
    return "Kannustaa itsenäiseen liikkumiseen ja tasapainon kehitykseen.";
  if (/tower|torni|jakkara|stool/.test(s))
    return "Nostaa lapsen turvallisesti mukaan keittiön ja kodin arkeen.";
  if (/iglu|soft|pehmo|palikka|ballpit|allas/.test(s))
    return "Pehmeää liikettä ja rakentelua olohuoneen lattialla.";
  if (/sänky|bed|patja|mattress/.test(s))
    return "Lapsen omaan mittakaavaan, oma tahti liikkumiseen.";
  if (/rekki|rack|hylly|shelf|laatikko|box/.test(s))
    return "Lapsen ulottuvilla — tukee itse tekemistä ja järjestystä.";
  return "Luonnollisia materiaaleja, jotka kasvavat lapsen mukana.";
}

function Index() {
  return (
    <Layout>
      {/* 1–2. HERO + TRUST STRIP */}
      <section className="relative isolate -mt-16 overflow-hidden bg-background md:-mt-20">
        <div className="grid lg:min-h-[720px] lg:grid-cols-[60%_40%]">
          <div className="relative order-1 h-[42vh] min-h-[300px] w-full lg:h-auto lg:min-h-0">
            <picture>
              <source
                media="(max-width: 767px)"
                type="image/avif"
                srcSet={`${heroWideAvifSm} 768w, ${heroWideAvifMd} 1200w`}
                sizes="100vw"
              />
              <source
                media="(min-width: 768px)"
                type="image/avif"
                srcSet={`${heroWideAvifMd} 1200w, ${heroWideAvifXl} 1440w, ${heroWideAvif} 1920w`}
                sizes="(min-width: 1024px) 60vw, 100vw"
              />
              <source
                media="(max-width: 767px)"
                type="image/webp"
                srcSet={`${heroWideWebpSm} 768w, ${heroWideWebpMd} 1200w`}
                sizes="100vw"
              />
              <source
                media="(min-width: 768px)"
                type="image/webp"
                srcSet={`${heroWideWebpMd} 1200w, ${heroWideWebpXl} 1440w, ${heroWideWebp} 1920w`}
                sizes="(min-width: 1024px) 60vw, 100vw"
              />
              <img
                src={heroWideWebp}
                alt="Taapero kiipeilee puisella Pikler-kiipeilykolmiolla auringon valaisemassa skandinaavisessa olohuoneessa"
                width={1920}
                height={1088}
                fetchPriority="high"
                decoding="async"
                className="h-full w-full object-cover object-[62%_center]"
              />
            </picture>
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-background/70 to-transparent"
            />
          </div>

          <div className="order-2 flex flex-col justify-center px-6 pb-10 pt-8 sm:px-10 lg:px-14 lg:py-24 xl:px-16">
            <div className="max-w-[460px]">
              <p className="text-[10px] font-medium uppercase tracking-[0.3em] text-foreground/80">
                Montessori24 · Suomi
              </p>
              <h1 className="mt-4 font-display text-[2.4rem] leading-[1.06] tracking-[-0.015em] text-foreground text-balance md:text-[3.4rem] lg:text-[3.7rem] xl:text-[4rem]">
                Kasvua leikin kautta.
                <br className="hidden sm:block" /> Joka päivä.
              </h1>
              <p className="mt-5 max-w-[42ch] text-pretty text-base leading-relaxed text-muted-foreground">
                Huolella valittuja Montessori-kalusteita, Pikler-kiipeilytelineitä ja
                kehittäviä leikkivälineitä 0–6-vuotiaille.
              </p>

              <div className="mt-7 flex flex-wrap items-center gap-3 sm:gap-4">
                <a
                  href="#ikaopas"
                  data-cta="hero_primary"
                  onClick={() =>
                    trackHeroCta({
                      variant: "A",
                      destination: "#ikaopas",
                      label: "Shoppaile iän mukaan",
                    })
                  }
                  className={ctaPrimary}
                >
                  Shoppaile iän mukaan
                </a>
                <a
                  href="#suosituimmat"
                  data-cta="hero_secondary"
                  onClick={() =>
                    trackHeroCta({
                      variant: "A",
                      destination: "#suosituimmat",
                      label: "Katso suosituimmat",
                    })
                  }
                  className={ctaSecondary}
                >
                  Katso suosituimmat
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* 3. TRUST STRIP — compact, 2×2 on mobile, one row on desktop */}
        <div className="border-y border-border bg-background">
          <ul className="container-prose grid grid-cols-2 gap-x-6 gap-y-4 py-4 lg:grid-cols-4">
            {[
              { Icon: Globe, label: "Suomalainen verkkokauppa" },
              { Icon: Truck, label: "Toimitus 3–7 päivässä" },
              { Icon: RotateCcw, label: "30 päivän palautusetu" },
              { Icon: ShieldCheck, label: "Turvallisuus huomioitu" },
            ].map(({ Icon, label }) => (
              <li key={label} className="flex items-center gap-2.5">
                <Icon className="h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden />
                <span className="text-[11px] font-medium uppercase leading-snug tracking-[0.12em] text-foreground/90">
                  {label}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* 4. SHOP BY AGE */}
      <div id="ikaopas" className="scroll-mt-20">
        <AgeCards />
      </div>

      {/* 5. SHOP BY CATEGORY */}
      <CategoryCards />

      {/* 6. BESTSELLERS */}
      <Suspense fallback={<TrendingSkeleton />}>
        <Bestsellers />
      </Suspense>

      {/* 6b. PIKLER PICKS — depth into the main category */}
      <Suspense fallback={<TrendingSkeleton />}>
        <PiklerFocus />
      </Suspense>

      {/* 7. LIFESTYLE */}
      <section className="cv-auto border-t border-border bg-sand" aria-labelledby="lifestyle-title">
        <div className="container-prose section-y-lg">
          <div className="grid items-center gap-10 md:grid-cols-2 md:gap-16">
            <div className="overflow-hidden rounded-3xl border border-border">
              <picture>
                <source type="image/avif" srcSet={lifestyleAvif as string} />
                <source type="image/webp" srcSet={lifestyleWebp as string} />
                <img
                  src={lifestyleWebp as string}
                  alt="Lapsi kiipeilee puisella Pikler-telineellä valoisassa skandinaavisessa olohuoneessa"
                  width={1200}
                  height={900}
                  loading="lazy"
                  decoding="async"
                  className="aspect-[4/3] w-full object-cover"
                />
              </picture>
            </div>
            <div>
              <p className="text-[11px] font-medium uppercase tracking-[0.24em] text-clay">
                Arjessa
              </p>
              <h2
                id="lifestyle-title"
                className="mt-3 font-display text-3xl italic text-foreground md:text-5xl text-balance"
              >
                Vähemmän tavaraa. Enemmän merkityksellistä leikkiä.
              </h2>
              <p className="mt-5 max-w-lg text-base leading-relaxed text-muted-foreground md:text-lg">
                Montessori-ympäristö kannustaa lasta liikkumaan, tutkimaan ja tekemään
                itse – omaan tahtiin.
              </p>
              <div className="mt-8">
                <Link to="/kauppa" className={ctaSecondary}>
                  Tutustu valikoimaan
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 8. MIKSI MONTESSORI24 */}
      <section className="cv-auto border-t border-border bg-background" aria-labelledby="why-m24-title">
        <div className="container-prose section-y">
          <div className="max-w-2xl">
            <p className="text-[11px] font-medium uppercase tracking-[0.24em] text-clay">
              Luottamus
            </p>
            <h2
              id="why-m24-title"
              className="mt-3 font-display text-3xl italic text-foreground md:text-5xl text-balance"
            >
              Miksi Montessori24?
            </h2>
          </div>
          <ul className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4 md:mt-12">
            {[
              {
                Icon: Sparkles,
                title: "Huolella valittu valikoima",
                body: "Tuotteita, joilla on selkeä tarkoitus lapsen kehityksessä.",
              },
              {
                Icon: Globe,
                title: "Eurooppalaisia valmistajia",
                body: "Panostamme laadukkaisiin ja pitkäikäisiin tuotteisiin.",
              },
              {
                Icon: ShieldCheck,
                title: "Turvallisuus näkyvästi esillä",
                body: "Tuotekohtaiset materiaalit, sertifikaatit ja turvallisuustiedot siellä missä niitä tarvitaan.",
              },
              {
                Icon: Truck,
                title: "Suomalainen verkkokauppa",
                body: "Montessori24.fi / MLR Solutions Oy",
              },
            ].map(({ Icon, title, body }) => (
              <li key={title} className="rounded-2xl border border-border bg-cream p-6">
                <Icon className="h-5 w-5 text-clay" strokeWidth={1.5} aria-hidden />
                <h3 className="mt-4 text-base font-medium text-foreground">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* 9. PIKLER COMPARISON */}
      <section
        className="cv-auto border-t border-border bg-sand"
        aria-labelledby="home-vertailu-title"
      >
        <div className="container-prose section-y">
          <div className="max-w-2xl">
            <p className="text-[11px] font-medium uppercase tracking-[0.24em] text-clay">
              Ostoapuri
            </p>
            <h2
              id="home-vertailu-title"
              className="mt-3 font-display text-3xl italic text-foreground md:text-5xl text-balance"
            >
              Mikä Pikler sopii meille?
            </h2>
            <p className="mt-4 text-base text-muted-foreground">
              Vertaa suosituimpia malleja ja löydä lapsellesi sopiva vaihtoehto.
            </p>
          </div>
          <div className="mt-10 md:mt-12">
            <Suspense fallback={<ComparisonSkeleton />}>
              <ProductComparison headingId="home-vertailu" />
            </Suspense>
          </div>
          <div className="mt-10 flex justify-center">
            <Link to="/vertailu" className={ctaSecondary}>
              Vertaa Pikler-malleja
            </Link>
          </div>
        </div>
      </section>

      {/* 11. MONTESSORI-KOULU */}
      <section className="cv-auto border-t border-border bg-background" aria-labelledby="blog-teaser-title">
        <div className="container-prose section-y">
          <div className="max-w-2xl">
            <p className="text-[11px] font-medium uppercase tracking-[0.24em] text-clay">
              Ilmainen tietopankki
            </p>
            <h2
              id="blog-teaser-title"
              className="mt-3 font-display text-3xl italic text-foreground md:text-5xl text-balance"
            >
              Montessori-koulu
            </h2>
            <p className="mt-4 text-base text-muted-foreground">
              Käytännöllisiä vinkkejä lapsen itsenäisyyden, keskittymisen ja oppimisen
              tukemiseen.
            </p>
          </div>

          {/* Uusin artikkeli — nostettu esiin */}
          <Link
            to="/montessori-koulu/montessori-suomessa-2026"
            className="group mt-10 grid overflow-hidden rounded-3xl border border-border bg-cream transition-all hover:border-foreground/40 hover:shadow-[0_22px_48px_-30px_rgb(41_43_40/0.45)] md:grid-cols-2"
          >
            <div className="aspect-[16/10] overflow-hidden bg-sand md:aspect-auto md:min-h-[320px]">
              <picture>
                <source type="image/avif" srcSet={newArticleAvif as string} />
                <source type="image/webp" srcSet={newArticleWebp as string} />
                <img
                  src={newArticleWebp as string}
                  alt="Vaalea skandinaavinen Montessori-luokkahuone ikkunanvalossa"
                  width={900}
                  height={506}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04] motion-reduce:transform-none"
                />
              </picture>
            </div>
            <div className="flex flex-col justify-center gap-4 p-7 md:p-10">
              <span className="inline-flex w-fit items-center gap-2 rounded-full bg-clay/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-clay">
                Uusi artikkeli
              </span>
              <h3 className="font-display text-2xl italic leading-tight text-foreground md:text-4xl text-balance">
                Montessori Suomessa 2026
              </h3>
              <p className="max-w-lg text-[15px] leading-relaxed text-muted-foreground">
                Montessori on Suomessa päiväkoteja, kunnallista perusopetusta, yläkoulua ja
                kansainvälistä opettajankoulutusta. Katso, mitä vuonna 2026 tapahtuu
                Helsingissä, Espoossa, Salossa ja muualla.
              </p>
              <span className="inline-flex items-center gap-2 text-sm font-medium text-foreground">
                <Clock className="h-3.5 w-3.5" aria-hidden /> 12 min lukuaika
                <span aria-hidden className="transition-transform group-hover:translate-x-1">→</span>
              </span>
            </div>
          </Link>

          <div className="mt-10 grid gap-5 md:mt-12 md:grid-cols-3 md:gap-6">
            {POSTS.filter((p) => p.featured && p.slug !== "montessori-suomessa-2026" && p.slug !== "nordic-montessori-konferenssi-2026").slice(0, 3).map((post) => (
              <Link
                key={post.to}
                to={post.to}
                className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-cream transition-all hover:border-foreground/40 hover:shadow-[0_18px_40px_-28px_rgb(41_43_40/0.4)]"
              >
                <div className="aspect-[16/9] overflow-hidden bg-sand">
                  <picture>
                    <source
                      type="image/avif"
                      srcSet={CATEGORY_HERO_CARD[post.category].avif}
                    />
                    <source
                      type="image/webp"
                      srcSet={CATEGORY_HERO_CARD[post.category].webp}
                    />
                    <img
                      src={CATEGORY_HERO_CARD[post.category].webp}
                      alt=""
                      width={720}
                      height={405}
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04] motion-reduce:transform-none"
                    />
                  </picture>
                </div>
                <div className="flex flex-1 flex-col justify-between p-6">
                  <div>
                    <span className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                      <Clock className="h-3 w-3" aria-hidden />
                      {post.minutes} min lukuaika
                    </span>
                    <h3 className="mt-3 font-display text-xl italic leading-tight text-foreground">
                      {post.title}
                    </h3>
                    <p className="mt-3 text-sm text-muted-foreground line-clamp-2">
                      {post.excerpt}
                    </p>
                  </div>
                  <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-foreground">
                    Lue artikkeli
                    <span aria-hidden className="transition-transform group-hover:translate-x-1">→</span>
                  </span>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-10 flex justify-center">
            <Link to="/montessori-koulu" className={ctaText}>
              Katso kaikki artikkelit <span aria-hidden>→</span>
            </Link>
          </div>
        </div>
      </section>

      {/* 12. FAQ */}
      <Suspense fallback={<div className="h-64" />}>
        <HomeFaq />
      </Suspense>

      <StickyMobileCta />
    </Layout>
  );
}

/**
 * Curated four-up grid — the homepage never shows a catalogue wall.
 * One standard product card, one tertiary "view all" link per section.
 */
function CuratedGrid({
  title,
  eyebrow,
  intro,
  products,
  viewAllTo,
  viewAllLabel,
}: {
  title: string;
  eyebrow: string;
  intro?: string;
  products: ShopifyProduct[];
  viewAllTo: string;
  viewAllLabel: string;
}) {
  if (products.length === 0) return null;
  const railKey = title.replace(/\.$/, "").toLowerCase();
  return (
    <div>
      <div className="max-w-2xl">
        {eyebrow ? (
          <p className="text-[11px] font-medium uppercase tracking-[0.24em] text-clay">
            {eyebrow}
          </p>
        ) : null}
        <h2 className="mt-3 font-display text-3xl italic text-foreground md:text-5xl text-balance">
          {title}
        </h2>
        {intro ? (
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">{intro}</p>
        ) : null}
      </div>

      <div className="mt-10 grid grid-cols-2 gap-x-5 gap-y-10 md:mt-12 md:gap-x-8 lg:grid-cols-4 lg:gap-x-10">
        {products.map((p, i) => (
          <ProductCard
            key={p.node.id}
            product={p}
            showAddToCart={false}
            showMeta
            benefit={benefitFor(p)}
            sizes="(min-width: 1280px) 320px, (min-width: 1024px) 24vw, (min-width: 640px) 45vw, 46vw"
            railName={railKey}
            railPosition={i}
          />
        ))}
      </div>

      <div className="mt-10 flex justify-center">
        <Link
          to={viewAllTo}
          className={ctaText}
          onClick={() => trackRailInteraction({ rail: railKey, action: "view_all" })}
        >
          {viewAllLabel} <span aria-hidden>→</span>
        </Link>
      </div>
    </div>
  );
}

/** Soft-play, tunnels and ball pits are not Pikler products. */
const NOT_PIKLER = /pehmo|soft|tunnel|tunneli|ballpit|allas|pallo|matto|mat\b|iglu|patja/i;

/**
 * Four representative Pikler picks — best overall, with ramp, compact and
 * larger/premium. Matching is by handle keyword with a stable fallback that
 * still excludes non-Pikler products, so the section never mis-categorises.
 */
function pickFourPikler(all: ShopifyProduct[]): ShopifyProduct[] {
  const pool = all.filter((p) => {
    const s = `${p.node.handle ?? ""} ${p.node.title}`;
    if (NOT_PIKLER.test(s)) return false;
    return /pikler|climb|kiipeily|triangle|kolmio|ramp|ramppi|arch|kaari/i.test(s);
  });
  const rules: RegExp[] = [
    /climbing-triangle(?!.*ramp)|kiipeilykolmio(?!.*ramp)/i,
    /ramp|ramppi|slide|liuku|2-in-1|arch/i,
    /fold|taitt|mini|compact|pieni/i,
    /xl|large|premium|set|deluxe|pro\b/i,
  ];
  const picked: ShopifyProduct[] = [];
  for (const rule of rules) {
    const hit = pool.find((p) => !picked.includes(p) && rule.test(p.node.handle ?? p.node.title));
    if (hit) picked.push(hit);
  }
  for (const p of pool) {
    if (picked.length >= 4) break;
    if (!picked.includes(p)) picked.push(p);
  }
  return picked.slice(0, 4);
}

/** Product family used to keep the bestseller row varied. */
function familyOf(p: ShopifyProduct): string {
  const s = `${p.node.handle ?? ""} ${p.node.title}`.toLowerCase();
  if (/pikler|kiipeily|climb|triangle|kolmio/.test(s)) return "pikler";
  if (/torni|tower|jakkara|stool/.test(s)) return "tower";
  if (/iglu|pehmo|soft|palikka|ballpit/.test(s)) return "softplay";
  if (/sänky|bed|patja/.test(s)) return "bed";
  if (/rekki|hylly|laatikko|rack|shelf/.test(s)) return "storage";
  return "muu";
}

/** One product per family first, so the row shows breadth, not four triangles. */
function pickDiverse(all: ShopifyProduct[], n: number): ShopifyProduct[] {
  const seen = new Set<string>();
  const first: ShopifyProduct[] = [];
  const rest: ShopifyProduct[] = [];
  for (const p of all) {
    const f = familyOf(p);
    if (seen.has(f)) rest.push(p);
    else {
      seen.add(f);
      first.push(p);
    }
  }
  return [...first, ...rest].slice(0, n);
}

function Bestsellers() {
  const q = useSuspenseQuery(bestsellerQueryOptions);
  const products = pickDiverse((q.data ?? []).filter(hasGoodImage), 4);
  if (products.length === 0) return null;
  return (
    <section
      id="suosituimmat"
      className="scroll-mt-20 border-t border-border bg-background"
      aria-label="Suosituimmat tuotteet"
    >
      <div className="container-prose section-y">
        <CuratedGrid
          title="Suosituimmat vanhempien valinnat"
          eyebrow="Valikoituja suosikkeja"
          products={products}
          viewAllTo="/kauppa"
          viewAllLabel="Katso kaikki suosikit"
        />
      </div>
    </section>
  );
}

function PiklerFocus() {
  const pikler = useSuspenseQuery(piklerProductsQueryOptions);
  const piklerProducts = pickFourPikler((pikler.data ?? []).filter(hasGoodImage));
  if (piklerProducts.length === 0) return null;

  return (
    <section className="cv-auto border-t border-border bg-sand" aria-label="Pikler-kokoelma">
      <div className="container-prose section-y">
        <CuratedGrid
          title="Neljä Pikleriä, joista aloittaa"
          eyebrow="Kiipeily & motoriikka"
          intro="Valikoituja malleja eri koteihin ja eri ikävaiheisiin — ei koko katalogia."
          products={piklerProducts}
          viewAllTo="/kiipeilyteline"
          viewAllLabel="Katso koko Pikler-mallisto"
        />
      </div>
    </section>
  );
}
